import { useGetIdentity } from '@refinedev/core'
import {
  Alert,
  Avatar,
  Button,
  Card,
  Form,
  Input,
  Modal,
  Radio,
  Space,
  Tag,
  Typography
} from 'antd'
import {
  CheckCircleOutlined,
  KeyOutlined,
  LockOutlined,
  SafetyOutlined,
  UserOutlined
} from '@ant-design/icons'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { PageHeader, PageShell } from '@/components/page-shell'
import { apiClient, normalizeError, tokenStorage } from '@/providers/api-client'
import { useMessage } from '@/providers/message-provider'
import { useThemeMode } from '@/theme/theme-provider'

const { Text, Paragraph } = Typography

/**
 * 设置页
 *
 * 分两大区块：
 *   1. 用户部分：展示登录管理员信息 + 修改密码入口（按钮 → Modal 弹窗）
 *   2. 基础设置：主题模式、会话信息、关于
 */

const sectionTitleStyle = {
  fontSize: 16,
  fontWeight: 600,
  marginBottom: 4,
  display: 'flex',
  alignItems: 'center',
  gap: 8
} as const

interface ChangePasswordFormValues {
  oldPassword: string
  newPassword: string
  confirmPassword: string
}

/** 从当前 JWT 中解析过期时间（仅用于展示） */
function getTokenExpiry(): string | null {
  const token = tokenStorage.get()
  if (!token) return null
  try {
    const payload = JSON.parse(atob(token.split('.')[1])) as { exp?: number }
    if (!payload.exp) return null
    return new Date(payload.exp * 1000).toLocaleString('zh-CN', { hour12: false })
  } catch {
    return null
  }
}

export default function SettingsPage() {
  const navigate = useNavigate()
  const message = useMessage()
  const { data: identity } = useGetIdentity<{ id: string; username: string; role: string }>()
  const { mode, setMode } = useThemeMode()
  const [form] = Form.useForm<ChangePasswordFormValues>()
  const [submitting, setSubmitting] = useState(false)
  const [modalOpen, setModalOpen] = useState(false)
  const [successVisible, setSuccessVisible] = useState(false)

  const tokenExpiry = getTokenExpiry()
  const roleLabel = identity?.role === 'super' ? '超级管理员' : identity?.role ?? '管理员'

  const handleSubmit = async (values: ChangePasswordFormValues) => {
    setSubmitting(true)
    setSuccessVisible(false)
    try {
      await apiClient.post('/api/admin/change-password', {
        oldPassword: values.oldPassword,
        newPassword: values.newPassword,
        confirmPassword: values.confirmPassword
      })
      message.success('密码修改成功')
      setSuccessVisible(true)
      form.resetFields()
      // 修改成功后延迟关闭弹窗，让用户看到成功状态
      setTimeout(() => {
        setModalOpen(false)
        setSuccessVisible(false)
      }, 1500)
    } catch (err) {
      const normalized = normalizeError(err)
      // 旧密码错误 → 标记 oldPassword 字段；其他错误用 message 提示
      if (normalized.code === 'UNAUTHORIZED' && /旧密码/.test(normalized.message)) {
        form.setFields([{ name: 'oldPassword', errors: [normalized.message] }])
      } else {
        message.error(normalized.message)
      }
    } finally {
      setSubmitting(false)
    }
  }

  const handleCancelModal = () => {
    setModalOpen(false)
    form.resetFields()
    setSuccessVisible(false)
  }

  return (
    <PageShell width="narrow">
      <PageHeader
        title="设置"
        subtitle="管理账户安全、主题偏好与当前会话信息。"
        onBack={() => navigate('/projects')}
      />

      {/* ── 用户部分 ─────────────────────────────── */}
      <Card style={{ marginBottom: 24 }} className="app-surface-card">
        <div style={sectionTitleStyle}>
          <UserOutlined style={{ color: 'var(--ant-color-primary)' }} />
          用户信息
        </div>
        <Text type="secondary" style={{ display: 'block', marginBottom: 20, fontSize: 13 }}>
          当前登录的管理员账户
        </Text>

        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 16,
            padding: 16,
            borderRadius: 8,
            background: 'var(--ant-color-fill-tertiary)'
          }}
        >
          <Avatar
            size={56}
            icon={<UserOutlined />}
            style={{ backgroundColor: 'var(--ant-color-primary)' }}
          />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
              <Text strong style={{ fontSize: 16 }}>
                {identity?.username ?? 'admin'}
              </Text>
              <Tag color={identity?.role === 'super' ? 'gold' : 'blue'}>{roleLabel}</Tag>
            </div>
            <Text type="secondary" style={{ fontSize: 12 }}>
              账户 ID：{identity?.id ?? '—'}
            </Text>
          </div>
          <Button
            type="default"
            icon={<KeyOutlined />}
            onClick={() => setModalOpen(true)}
          >
            修改密码
          </Button>
        </div>
      </Card>

      {/* ── 基础设置 ─────────────────────────────── */}
      <Card className="app-surface-card">
        <div style={sectionTitleStyle}>
          <SafetyOutlined style={{ color: 'var(--ant-color-primary)' }} />
          基础设置
        </div>
        <Text type="secondary" style={{ display: 'block', marginBottom: 20, fontSize: 13 }}>
          界面偏好与会话信息
        </Text>

        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 16
          }}
        >
          {/* 主题模式 */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '12px 0',
              borderBottom: '1px solid var(--ant-color-border-secondary)'
            }}
          >
            <div>
              <Text strong>主题模式</Text>
              <Paragraph type="secondary" style={{ fontSize: 12, marginBottom: 0, marginTop: 2 }}>
                切换亮色 / 暗色界面主题
              </Paragraph>
            </div>
            <Radio.Group
              value={mode}
              onChange={(e) => setMode(e.target.value)}
              optionType="button"
              buttonStyle="solid"
            >
              <Radio.Button value="light">亮色</Radio.Button>
              <Radio.Button value="dark">暗色</Radio.Button>
            </Radio.Group>
          </div>

          {/* 会话信息 */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '12px 0',
              borderBottom: '1px solid var(--ant-color-border-secondary)'
            }}
          >
            <div>
              <Text strong>会话有效期</Text>
              <Paragraph type="secondary" style={{ fontSize: 12, marginBottom: 0, marginTop: 2 }}>
                当前登录 Token 的自然过期时间
              </Paragraph>
            </div>
            <Text code style={{ fontFamily: 'JetBrains Mono, monospace' }}>
              {tokenExpiry ?? '—'}
            </Text>
          </div>

          {/* 关于 */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '12px 0'
            }}
          >
            <div>
              <Text strong>关于</Text>
              <Paragraph type="secondary" style={{ fontSize: 12, marginBottom: 0, marginTop: 2 }}>
                License Platform Admin
              </Paragraph>
            </div>
            <Tag color="green">v1.0</Tag>
          </div>
        </div>
      </Card>

      {/* ── 修改密码弹窗 ─────────────────────────────── */}
      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <LockOutlined style={{ color: 'var(--ant-color-primary)' }} />
            修改密码
          </div>
        }
        open={modalOpen}
        onCancel={handleCancelModal}
        footer={null}
        destroyOnHidden
        width={460}
        maskClosable={!submitting}
      >
        <Text type="secondary" style={{ display: 'block', marginBottom: 20, fontSize: 13 }}>
          新密码需 8 位以上，且同时包含字母与数字
        </Text>

        {successVisible && (
          <Alert
            type="success"
            showIcon
            icon={<CheckCircleOutlined />}
            message="密码修改成功"
            description="当前登录会话仍有效，下次登录请使用新密码。"
            style={{ marginBottom: 16 }}
            closable
            onClose={() => setSuccessVisible(false)}
          />
        )}

        <Form<ChangePasswordFormValues>
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          requiredMark="optional"
          disabled={submitting}
        >
          <Form.Item
            name="oldPassword"
            label="旧密码"
            rules={[{ required: true, message: '请输入旧密码' }]}
          >
            <Input.Password
              prefix={<LockOutlined style={{ color: 'var(--ant-color-text-quaternary)' }} />}
              placeholder="请输入当前密码"
              size="large"
              autoComplete="current-password"
            />
          </Form.Item>

          <Form.Item
            name="newPassword"
            label="新密码"
            rules={[
              { required: true, message: '请输入新密码' },
              { min: 8, message: '新密码至少 8 位' },
              {
                validator: (_, value: string) => {
                  if (!value) return Promise.resolve()
                  if (!/[A-Za-z]/.test(value)) return Promise.reject(new Error('新密码必须包含字母'))
                  if (!/[0-9]/.test(value)) return Promise.reject(new Error('新密码必须包含数字'))
                  return Promise.resolve()
                }
              }
            ]}
            extra="至少 8 位，必须同时包含字母和数字"
          >
            <Input.Password
              prefix={<SafetyOutlined style={{ color: 'var(--ant-color-text-quaternary)' }} />}
              placeholder="请输入新密码"
              size="large"
              autoComplete="new-password"
            />
          </Form.Item>

          <Form.Item
            name="confirmPassword"
            label="确认新密码"
            dependencies={['newPassword']}
            rules={[
              { required: true, message: '请再次输入新密码' },
              ({ getFieldValue }) => ({
                validator(_, value: string) {
                  if (!value || getFieldValue('newPassword') === value) {
                    return Promise.resolve()
                  }
                  return Promise.reject(new Error('两次输入的新密码不一致'))
                }
              })
            ]}
          >
            <Input.Password
              prefix={<SafetyOutlined style={{ color: 'var(--ant-color-text-quaternary)' }} />}
              placeholder="请再次输入新密码"
              size="large"
              autoComplete="new-password"
            />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, marginTop: 8 }}>
            <Space>
              <Button type="primary" htmlType="submit" loading={submitting} style={{ fontWeight: 500 }}>
                确认修改
              </Button>
              <Button onClick={() => form.resetFields()} disabled={submitting}>
                重置
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </PageShell>
  )
}
